import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { history } from "../../utils/History";
import {
  buildWinnerFormData,
  createBroadcastPreviewModel,
  getRequestErrorMessage,
  normalizeRecipients,
} from "../../utils/winnerWorkflow";
import useUnsavedChangesGuard from "../../components/hooks/useUnsavedChangesGuard";
import { toastError, toastSuccess } from "../../components/commons/toast";
import Promo from "../../store/actions/promotion/Index";
import PreviewBroadcastModal from "./PreviewBroadcastModal";
import BroadcastConfirmationModal from "./BroadcastConfirmationModal";

const WinnerReview = ({
  promotionId,
  promo,
  winners = [],
  isLoading = false,
  loadError,
  backPath,
  title = "Hasil Generate Pemenang",
}) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const [completed, setCompleted] = useState(false);
  const savingRef = useRef(false);
  const broadcastingRef = useRef(false);

  const recipients = useMemo(
    () => normalizeRecipients(winners, promo),
    [winners, promo]
  );
  const previewModel = useMemo(
    () => createBroadcastPreviewModel(recipients, promo),
    [recipients, promo]
  );
  const isBusy = isSaving || isBroadcasting;
  const hasUnsavedResult = recipients.length > 0 && !completed;
  const allowNavigation = useUnsavedChangesGuard(hasUnsavedResult);

  const showActionError = (error, fallback) => {
    const message = getRequestErrorMessage(error, fallback);
    setActionError(message);
    toastError(message);
  };

  const handleSave = async () => {
    if (savingRef.current || broadcastingRef.current || previewModel.blockers.length) return;

    savingRef.current = true;
    setActionError("");
    setIsSaving(true);
    try {
      await dispatch(
        Promo.saveWinner(buildWinnerFormData(recipients, promotionId))
      );
      setCompleted(true);
      allowNavigation();
      toastSuccess("Pemenang berhasil disimpan.");
      history.replace(`/promo-winner/${promotionId}`);
    } catch (error) {
      showActionError(error, "Pemenang gagal disimpan. Silakan coba lagi.");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleBroadcast = async () => {
    if (savingRef.current || broadcastingRef.current || previewModel.blockers.length) return;

    broadcastingRef.current = true;
    setActionError("");
    setIsBroadcasting(true);
    try {
      await dispatch(
        Promo.saveBroadcast(
          buildWinnerFormData(previewModel.eligibleRecipients, promotionId)
        )
      );
      setCompleted(true);
      allowNavigation();
      toastSuccess(
        `Broadcast ke ${previewModel.eligibleRecipients.length} pemenang berhasil.`
      );
      history.replace(`/promo-winner/${promotionId}`);
    } catch (error) {
      showActionError(error, "Broadcast pemenang gagal. Silakan coba lagi.");
    } finally {
      broadcastingRef.current = false;
      setIsBroadcasting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="menu-container">
        <div className="content-container-side text-center py-5">
          <span className="spinner-border mr-2" aria-hidden="true" />
          Menyiapkan hasil generate...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="menu-container">
        <div className="content-container-side py-5 text-center">
          <div className="alert alert-danger" role="alert">{loadError}</div>
          <button type="button" className="button-batal" onClick={() => history.replace(backPath)}>
            Kembali
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="menu-container">
      <div className="header-generate-result">
        <div className="title"><b>{title}</b></div>
        <p>Periksa penerima dan voucher promo {promo?.name || "-"} sebelum disimpan atau dibroadcast.</p>
      </div>

      <div className="content-container-side">
        {previewModel.blockers.map((message) => (
          <div className="alert alert-danger" role="alert" key={message}>{message}</div>
        ))}
        {previewModel.warnings.map((message) => (
          <div className="alert alert-warning" role="alert" key={message}>{message}</div>
        ))}
        {actionError && <div className="alert alert-danger" role="alert">{actionError}</div>}

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>No</th>
                <th>Nama Pemenang</th>
                <th>No WhatsApp</th>
                <th>Toko/Outlet</th>
                <th>Voucher</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((winner, index) => (
                <tr key={`${winner.winnerId || winner.id}-${index}`}>
                  <td>{index + 1}</td>
                  <td>{winner.fullName}</td>
                  <td>{winner.phoneNumber || "-"}</td>
                  <td>{winner.storeName}</td>
                  <td>{winner.voucherCode || "-"}</td>
                </tr>
              ))}
              {!recipients.length && (
                <tr><td colSpan="5" className="text-center">Tidak ada hasil generate.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="button-generated-result">
          <button
            type="button"
            className="button-batal"
            disabled={isBusy}
            onClick={() => history.push(backPath)}
          >
            Batal Generate
          </button>
          <button
            type="button"
            className="button-simpan"
            disabled={isBusy || previewModel.blockers.length > 0}
            onClick={() => {
              setActionError("");
              setPreviewOpen(true);
            }}
          >
            Broadcast
          </button>
          <button
            type="button"
            className="button-simpan"
            disabled={isBusy || previewModel.blockers.length > 0}
            onClick={handleSave}
          >
            {isSaving && <span className="spinner-border spinner-border-sm mr-2" aria-hidden="true" />}
            {isSaving ? "Sedang menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>

      <PreviewBroadcastModal
        show={previewOpen}
        toggle={() => !isBusy && setPreviewOpen(false)}
        confirm={() => {
          setPreviewOpen(false);
          setConfirmationOpen(true);
        }}
        model={previewModel}
        isLoading={false}
        error=""
      />
      <BroadcastConfirmationModal
        show={confirmationOpen}
        count={previewModel.eligibleRecipients.length}
        cancel={() => !isBroadcasting && setConfirmationOpen(false)}
        confirm={handleBroadcast}
        isBroadcasting={isBroadcasting}
        error={actionError}
      />
    </div>
  );
};

export default WinnerReview;
